'use server'

import { apiRequest, ApiResponse } from '../request'

export interface ContractTerm {
    id: number
    contractId: number
    number: string
    title: string
    content: string
    type: 'CLAUSE' | 'PARAGRAPH' | 'INCISO' | 'ALINEA' | 'HEADER' | 'FOOTER'
    status: 'APPROVED' | 'REJECTED' | 'PENDING'
    fixed: boolean
    adminOnly: boolean
    createdAt: string
    updatedAt: string
}

export interface Contract {
    id: number
    companyId: number
    partnerId?: number
    status: 'DRAFT' | 'IN_ANALYSIS' | 'SENT' | 'APPROVED' | 'REJECTED' | 'SIGNED'
    createdByType: 'COMPANY' | 'PARTNER'
    terms: ContractTerm[]
    partner?: { id: number; name: string; tradingName?: string }
    createdAt: string
    updatedAt: string
}

export interface CreateContractTermData {
    number: string
    title: string
    content: string
    fixed?: boolean
    adminOnly?: boolean
    type?: ContractTerm['type']
    status?: ContractTerm['status']
}

export interface UpdateContractTermData {
    number?: string
    title?: string
    content?: string
    fixed?: boolean
    adminOnly?: boolean
    type?: ContractTerm['type']
    status?: ContractTerm['status']
}

export interface CreateContractData {
    terms?: CreateContractTermData[]
}

export interface UpdateContractData {
    status?: Contract['status']
}

// --- Contracts ---

export async function getContracts(
    companyId: number,
    params?: { page?: number; limit?: number; status?: string; search?: string },
): Promise<ApiResponse<Contract[]>> {
    return apiRequest<Contract[]>(`contracts/company/${companyId}`, {
        params,
    })
}

export async function getContract(id: number): Promise<ApiResponse<Contract>> {
    return apiRequest<Contract>(`contracts/${id}`, {})
}

export async function createContract(
    data?: CreateContractData,
): Promise<ApiResponse<Contract>> {
    return apiRequest<Contract>('contracts', { data: data || {} }, 'post')
}

export async function updateContract(
    id: number,
    data: UpdateContractData,
): Promise<ApiResponse<Contract>> {
    return apiRequest<Contract>(`contracts/${id}`, { data }, 'patch')
}

export async function deleteContract(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`contracts/${id}`, {}, 'delete')
}

// --- Status ---

export async function updateContractStatus(
    id: number,
    status: Contract['status'],
): Promise<ApiResponse<Contract>> {
    return apiRequest<Contract>(
        `contracts/${id}/status`,
        { data: { status } },
        'patch',
    )
}

// --- Terms ---

export async function addContractTerm(
    contractId: number,
    data: CreateContractTermData,
): Promise<ApiResponse<ContractTerm>> {
    return apiRequest<ContractTerm>(
        `contracts/${contractId}/terms`,
        { data },
        'post',
    )
}

export async function updateContractTerm(
    termId: number,
    data: UpdateContractTermData,
): Promise<ApiResponse<ContractTerm>> {
    return apiRequest<ContractTerm>(
        `contracts/terms/${termId}`,
        { data },
        'patch',
    )
}

export async function deleteContractTerm(
    termId: number,
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`contracts/terms/${termId}`, {}, 'delete')
}
